import React, { useMemo, useState } from "react";
import { Maximize2, Minimize2 } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "../ui/Card";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../ui/Table";
import { Button } from "../ui/Button";
import { formatNumber, formatDecimal, formatSignedNumber, formatCurrency, deltaTextClass } from "../FinancialReview/format";
import { KpiCard, TablePager, usePagedRows } from "../FinancialReview/ui";
import { downloadXlsx } from "../FinancialReview/xlsxExport";
import { financialReviewExportSheets } from "./exportSheets";

const MAX_CHART_ORGS = 15;
const ROW_GROUP_KEYS = ["super_org", "specialty", "top_specialty"];
const EMPTY_BREAKDOWN = [];

function ExpandableTableCard({ title, description, expanded, onToggleExpand, children }) {
  return (
    <Card className={expanded ? "lg:col-span-full" : undefined}>
      <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
        <div className="min-w-0 flex-1">
          <CardTitle className="text-base">{title}</CardTitle>
          {description && <CardDescription className="mt-1 truncate">{description}</CardDescription>}
        </div>
        <Button variant="outline" size="sm" onClick={onToggleExpand} className="shrink-0 gap-1">
          {expanded ? (
            <>
              <Minimize2 className="h-4 w-4" /> Collapse
            </>
          ) : (
            <>
              <Maximize2 className="h-4 w-4" /> Expand
            </>
          )}
        </Button>
      </CardHeader>
      <CardContent className="pt-0">{children}</CardContent>
    </Card>
  );
}

function BreakdownTable({ pager, labelKey, labelHeader, emptyLabel, showPricing }) {
  return (
    <>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{labelHeader}</TableHead>
              <TableHead>Licensed</TableHead>
              <TableHead>Licensed Δ</TableHead>
              <TableHead>Monthly recordings</TableHead>
              <TableHead>Cumulative recordings</TableHead>
              <TableHead>Avg recordings daily</TableHead>
              {showPricing && (
                <>
                  <TableHead>Cost / user</TableHead>
                  <TableHead>Revenue</TableHead>
                </>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {pager.pageRows.map((row) => (
              <TableRow key={row[labelKey]}>
                <TableCell>{row[labelKey]}</TableCell>
                <TableCell>{formatNumber(row.licensed)}</TableCell>
                <TableCell className={deltaTextClass(row.mom_licensed_delta)}>
                  {formatSignedNumber(row.mom_licensed_delta)}
                </TableCell>
                <TableCell>{formatNumber(row.recordings)}</TableCell>
                <TableCell>{formatNumber(row.cumulative_recordings)}</TableCell>
                <TableCell>{formatDecimal(row.avg_recordings_daily)}</TableCell>
                {showPricing && (
                  <>
                    <TableCell>{formatCurrency(row.cost_per_user)}</TableCell>
                    <TableCell>{formatCurrency(row.revenue)}</TableCell>
                  </>
                )}
              </TableRow>
            ))}
            {pager.pageRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={showPricing ? 8 : 6} className="text-center text-muted-foreground">
                  {emptyLabel}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <TablePager page={pager.page} totalPages={pager.totalPages} onPageChange={pager.setPage} />
    </>
  );
}

function momChartRows(rows, gaining) {
  return rows
    .filter((row) => (gaining ? row.mom_licensed_delta > 0 : row.mom_licensed_delta < 0))
    .sort((a, b) =>
      gaining ? b.mom_licensed_delta - a.mom_licensed_delta : a.mom_licensed_delta - b.mom_licensed_delta
    )
    .slice(0, MAX_CHART_ORGS)
    .map((row) => ({ super_org: row.super_org, change: Math.abs(row.mom_licensed_delta) }));
}

function MomOrgsChart({ rows, direction }) {
  const gaining = direction === "up";
  const chartRows = useMemo(() => momChartRows(rows, gaining), [rows, gaining]);
  const metric = gaining ? "Licenses gained" : "Licenses lost";

  return (
    <div className="min-w-0">
      <p className="text-sm font-medium mb-2">{gaining ? "Gaining" : "Losing"}</p>
      {chartRows.length === 0 ? (
        <p className="text-sm text-muted-foreground">None vs prior month</p>
      ) : (
        <div style={{ height: Math.min(480, Math.max(160, chartRows.length * 36)) }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartRows} layout="vertical" margin={{ top: 8, right: 16, left: 8, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" allowDecimals={false} />
              <YAxis
                type="category"
                dataKey="super_org"
                width={140}
                tick={{ fontSize: 12 }}
                tickFormatter={(value) => (value.length > 20 ? `${value.slice(0, 18)}…` : value)}
              />
              <Tooltip formatter={(value) => [formatNumber(value), metric]} labelFormatter={(label) => label} />
              <Bar dataKey="change" name={metric} fill={gaining ? "#16a34a" : "#dc2626"} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

function CleaningNeededCard({ label, count, sublabel, onOpen }) {
  return (
    <Card className="lg:w-56 shrink-0">
      <CardHeader className="pb-2">
        <CardDescription>{label}</CardDescription>
      </CardHeader>
      <CardContent className="pt-0 space-y-3">
        <p className="text-2xl font-semibold">{formatNumber(count)}</p>
        {sublabel && <p className="text-xs text-muted-foreground">{sublabel}</p>}
        {onOpen && (
          <Button variant="outline" size="sm" className="w-full" onClick={onOpen}>
            Open
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

export default function FinancialReviewTab({ data, loading, error, onOpenCleaning }) {
  const orgRows = useMemo(
    () => (data?.by_super_org || EMPTY_BREAKDOWN).filter((row) => row.licensed > 0),
    [data?.by_super_org]
  );
  const specialtyRows = useMemo(
    () => (data?.by_specialty || EMPTY_BREAKDOWN).filter((row) => row.licensed > 0),
    [data?.by_specialty]
  );
  const topSpecialtyRows = data?.top_specialty_by_super_org || EMPTY_BREAKDOWN;
  const orgPager = usePagedRows(orgRows);
  const specialtyPager = usePagedRows(specialtyRows);
  const topSpecialtyPager = usePagedRows(topSpecialtyRows);
  const [expandedCards, setExpandedCards] = useState({});
  const toggleExpand = (key) =>
    setExpandedCards((prev) => {
      const next = !prev[key];
      const cleared = Object.fromEntries(ROW_GROUP_KEYS.map((k) => [k, false]));
      return { ...cleared, [key]: next };
    });

  if (loading && !data) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (error) return <p className="text-sm text-red-600">{error}</p>;
  if (!data) return null;

  const { summary, report_month, previous_report_month, by_super_org, by_specialty, licensed_users, top_specialty_by_super_org } =
    data;
  const expandedRowKey = ROW_GROUP_KEYS.find((key) => expandedCards[key]);

  const handleDownloadXlsx = () => {
    downloadXlsx(
      `financial-review-${report_month || "report"}.xlsx`,
      financialReviewExportSheets({
        licensed_users,
        by_super_org,
        by_specialty,
        top_specialty_by_super_org,
        summary,
        report_month,
        previous_report_month,
      })
    );
  };

  const rowCards = {
    super_org: (
      <ExpandableTableCard
        title="By super org"
        description={orgPager.total ? `${orgPager.total} super orgs match the current filters.` : ""}
        expanded={!!expandedCards.super_org}
        onToggleExpand={() => toggleExpand("super_org")}
      >
        <BreakdownTable
          pager={orgPager}
          labelKey="super_org"
          labelHeader="Super org"
          emptyLabel="No mapped users"
          showPricing
        />
      </ExpandableTableCard>
    ),
    specialty: (
      <ExpandableTableCard
        title="By specialty"
        description={specialtyPager.total ? `${specialtyPager.total} specialties match the current filters.` : ""}
        expanded={!!expandedCards.specialty}
        onToggleExpand={() => toggleExpand("specialty")}
      >
        <BreakdownTable
          pager={specialtyPager}
          labelKey="specialty"
          labelHeader="Specialty"
          emptyLabel="No mapped users"
        />
      </ExpandableTableCard>
    ),
    top_specialty: (
      <ExpandableTableCard
        title="Top specialty per super org"
        description={
          topSpecialtyPager.total
            ? `Highest-licensed specialty in each of ${topSpecialtyPager.total} super orgs.`
            : "Highest-licensed specialty in each super org."
        }
        expanded={!!expandedCards.top_specialty}
        onToggleExpand={() => toggleExpand("top_specialty")}
      >
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Super org</TableHead>
                <TableHead>Top specialty</TableHead>
                <TableHead>Licensed</TableHead>
                <TableHead>Monthly recordings</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {topSpecialtyPager.pageRows.map((row) => (
                <TableRow key={row.super_org}>
                  <TableCell>{row.super_org}</TableCell>
                  <TableCell>{row.specialty}</TableCell>
                  <TableCell>{formatNumber(row.licensed)}</TableCell>
                  <TableCell>{formatNumber(row.recordings)}</TableCell>
                </TableRow>
              ))}
              {topSpecialtyPager.pageRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} className="text-center text-muted-foreground">
                    No mapped users
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        <TablePager
          page={topSpecialtyPager.page}
          totalPages={topSpecialtyPager.totalPages}
          onPageChange={topSpecialtyPager.setPage}
        />
      </ExpandableTableCard>
    ),
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold">Financial Review</h2>
        <p className="text-sm text-muted-foreground">
          {report_month} — licensed is distinct users with recordings that month. Specialty and super org
          come from revops cleanup.
        </p>
        <div className="flex flex-wrap gap-2 mt-3">
          <Button variant="outline" onClick={handleDownloadXlsx}>
            Download XLSX
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        <KpiCard
          label="Licensed"
          value={formatNumber(summary.licensed)}
          sublabel={`Distinct activity users with recordings in ${report_month}`}
        />
        <KpiCard
          label="Monthly recordings"
          value={formatNumber(summary.recordings)}
          sublabel={`Across all in ${report_month}`}
        />
        <KpiCard
          label="Cumulative recordings"
          value={formatNumber(summary.cumulative_recordings)}
          sublabel={`Through ${report_month}`}
        />
        <KpiCard
          label="Avg recordings daily"
          value={formatDecimal(summary.avg_recordings_daily)}
          sublabel={`Monthly recordings / days in ${report_month}`}
        />
        <KpiCard label="Avg DAU" value={formatDecimal(summary.avg_dau)} sublabel={report_month} />
        <KpiCard
          label={`Licensed vs. ${previous_report_month}`}
          value={
            <span className={deltaTextClass(summary.mom_licensed_delta)}>
              {formatSignedNumber(summary.mom_licensed_delta)}
            </span>
          }
        />
        <KpiCard
          label="Revenue"
          value={formatCurrency(summary.revenue)}
          sublabel={
            summary.priced_org_count
              ? `${summary.priced_org_count} priced super org${summary.priced_org_count === 1 ? "" : "s"} × licensed`
              : "Priced super orgs × licensed this month"
          }
        />
      </div>

      {expandedRowKey ? (
        <>
          <div>{rowCards[expandedRowKey]}</div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {ROW_GROUP_KEYS.filter((key) => key !== expandedRowKey).map((key) => (
              <React.Fragment key={key}>{rowCards[key]}</React.Fragment>
            ))}
          </div>
        </>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {rowCards.super_org}
          {rowCards.specialty}
          {rowCards.top_specialty}
        </div>
      )}

      <div className="flex flex-col lg:flex-row gap-4 items-start">
        <Card className="flex-1 min-w-0 w-full">
          <CardHeader>
            <CardTitle className="text-base">License change by super org</CardTitle>
            <CardDescription>vs {previous_report_month}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <MomOrgsChart rows={by_super_org || []} direction="down" />
              <MomOrgsChart rows={by_super_org || []} direction="up" />
            </div>
          </CardContent>
        </Card>
        <CleaningNeededCard
          label="Cleaning needed"
          count={(summary.cleaning_needed || 0) + (summary.pricing_cleaning_needed || 0)}
          onOpen={onOpenCleaning}
        />
      </div>
    </div>
  );
}
